import React, { useState } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Typography } from "@mui/material";
import { isEmpty } from "lodash";
import useApi from "../hooks/useApi";

/**
 * ApplicationURIAddModal component used for adding a new application URI.
 *
 * @component
 *
 * @param {Object} props - The component props.
 * @param {Object} props.config - Configuration object.
 * @param {Object} props.config.uri - URI object.
 * @param {string} props.config.uri.base - Base URI string.
 * @param {boolean} props.isOpen - Boolean flag to show/hide the modal.
 * @param {function} props.onChangeBulk - Parent callback to handle bulk updates.
 * @param {string|number} props.environmentId - The environment ID.
 *
 * @returns {JSX.Element} The rendered component.
 */
const ApplicationURIAddModal = (props) => {
	const {
		config,
		isOpen,
		onChangeBulk: parentOnChangeBulk,
		environmentId
	} = props;

	const [values, setValues] = useState({
		applicationURIAddName: "",
		applicationURIAddURI: ""
	});
	const [errors, setErrors] = useState({});

	const api = useApi();

	/**
	 * Handles value change for a specific field.
	 *
	 * @param {string} value - The new value for the field.
	 * @param {string} field - The field being updated.
	 * @returns {void}
	 */
	const handleChange = (value, field) => {
		setValues((prev) => ({ ...prev, [field]: value }));
	};

	/**
	 * Merges bulk changes to the current form values.
	 *
	 * @param {Object} data - The data to merge into form values.
	 * @returns {void}
	 */
	const handleChangeBulk = (data) => {
		setValues((prev) => ({ ...prev, ...data }));
	};

	/**
	 * Sets form error messages.
	 *
	 * @param {Object} errorObj - An object containing error messages keyed by field name.
	 * @returns {void}
	 */
	const handleInvalidForm = (errorObj) => {
		setErrors(errorObj);
	};

	/**
	 * Resets all form error messages.
	 *
	 * @returns {void}
	 */
	const resetFormErrors = () => {
		setErrors({});
	};

	/**
	 * Handles modal close event by resetting form values and errors.
	 *
	 * @returns {void}
	 */
	const handleClose = () => {
		resetFormErrors();
		handleChangeBulk({
			applicationURIAddName: "",
			applicationURIAddURI: ""
		});
		parentOnChangeBulk({ applicationURIAddModalIsOpen: false });
	};

	/**
	 * Handles form submission by validating inputs and sending a POST request.
	 *
	 * @async
	 * @returns {Promise<void>}
	 */
	const handleSubmit = async () => {
		const currentErrors = {};
		if (!values.applicationURIAddName)
			currentErrors.applicationURIAddName = "You must supply a URI name (i.e. key) for the property key";
		if (!values.applicationURIAddURI)
			currentErrors.applicationURIAddURI = "You must supply a URI for the property value";
		if (!isEmpty(currentErrors)) return handleInvalidForm(currentErrors);

		try {
			const application = await api.environmentURI.add({
				environmentId: environmentId,
				data: {
					uriKey: values.applicationURIAddName,
					uriValue: values.applicationURIAddURI
				}
			});

			handleChangeBulk({
				applicationURIAddName: "",
				applicationURIAddURI: ""
			});

			parentOnChangeBulk({
				applicationURIAddModalIsOpen: false,
				application: application,
				environments: application.environments
			});
		} catch (error) {
			console.error("Failed to add environment URI:", error);
		}
	};

	return (
		<Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
			<DialogTitle>Add New Application URI</DialogTitle>
			<DialogContent>
				<Typography variant="subtitle1" gutterBottom>
					Add a new application URI that the API/UI will be aware of in its context
				</Typography>
				<Stack direction="column" spacing={2} mt={1}>
					<TextField
						label="Name (or key) of URI prop"
						value={values.applicationURIAddName}
						error={Boolean(errors.applicationURIAddName)}
						helperText={errors.applicationURIAddName}
						onChange={(e) => {
							let value = e.target.value.toLowerCase();
							value = value.replace(/\s/g, "");
							handleChange(value, "applicationURIAddName");
						}}
					/>
					<TextField
						label="URI"
						value={values.applicationURIAddURI}
						error={Boolean(errors.applicationURIAddURI)}
						helperText={errors.applicationURIAddURI}
						onChange={(e) => handleChange(e.target.value, "applicationURIAddURI")}
					/>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} color="secondary">
					Cancel
				</Button>
				<Button onClick={handleSubmit} variant="contained">
					Submit
				</Button>
			</DialogActions>
		</Dialog>
	);
};

ApplicationURIAddModal.propTypes = {
	config: PropTypes.shape({
		uri: PropTypes.shape({
			base: PropTypes.string.isRequired
		}).isRequired
	}).isRequired,
	isOpen: PropTypes.bool.isRequired,
	onChangeBulk: PropTypes.func.isRequired,
	environmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default ApplicationURIAddModal;
